import React from 'react';
import useResponsive from '../hooks/useResponsive';
import { Box, Tooltip, Typography } from '@mui/material';

const MetricTitle = ({ item }) => {
  const titleEl = (
    <span
      className="analytics-card-title"
      style={
        item.description
          ? { cursor: "help", borderBottom: "1px dotted #94a3b8" }
          : undefined
      }
    >
      {item.title}
    </span>
  );

  if (!item.description) return titleEl;

  return (
    <Tooltip
      arrow
      placement="top"
      enterDelay={250}
      title={
        <Box sx={{ maxWidth: 380, py: 0.5 }}>
          <Typography variant="body2" sx={{ fontSize: 13 }}>
            {item.description}
          </Typography>
          {item.example && (
            <Typography
              variant="caption"
              component="div"
              sx={{ mt: 1, display: "block", opacity: 0.92 }}
            >
              <strong>Example:</strong> {item.example}
            </Typography>
          )}
        </Box>
      }
    >
      {titleEl}
    </Tooltip>
  );
};

const ResponsiveAnalytics = ({ 
  title = "Analytics",
  data = [],
  className = '',
  showTitle = true
}) => {
  const { isMobile, isTablet } = useResponsive();

  const getGridColumns = () => {
    // Mobile: show cards in 2 columns
    if (isMobile) return 'repeat(2, minmax(0, 1fr))';
    if (isTablet) return 'repeat(2, 1fr)';
    return 'repeat(auto-fit, minmax(200px, 1fr))';
  };

  const formatValue = (value, type) => {
    if (type === 'currency') {
      return `₹ ${value}`;
    }
    if (type === 'percentage') {
      return `${value}%`;
    }
    return value;
  };

  return (
    <div className={`responsive-analytics-container ${className}`}>
      {showTitle && title && (
        <div className="analytics-header">
          <Typography 
            variant="h5" 
            className="analytics-title"
            style={{
              fontSize: isMobile ? 'var(--font-size-lg)' : 'var(--font-size-2xl)',
              marginBottom: 'var(--spacing-lg)',
              color: '#333',
              fontWeight: '600'
            }}
          >
            {title}
          </Typography>
        </div>
      )}

      <div 
        className="analytics-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: getGridColumns(),
          gap: isMobile ? 'var(--spacing-md)' : 'var(--spacing-lg)',
          marginTop: showTitle && title ? 'var(--spacing-lg)' : '0'
        }}
      >
        {data.map((item, index) => (
          <div key={item.key || index} className="analytics-card">
            <div className="analytics-card-content">
              <div className="analytics-card-header">
                <MetricTitle item={item} />
              </div>
              <div className="analytics-card-value">
                <b className="analytics-value">
                  {formatValue(item.value, item.type)}
                </b>
              </div>
              {item.subtitle && (
                <div className="analytics-card-subtitle">
                  <span className="analytics-subtitle">
                    {item.subtitle}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ResponsiveAnalytics;
